/**
 * Industry Watch — five lenses on the AI industry not captured by the other
 * frontier pages: lawsuits, departures, open letters, funding rounds, and
 * compute / datacenter buildouts.
 *
 * Each lens reads its own endpoint:
 *   /api/frontier/lawsuits
 *   /api/frontier/departures
 *   /api/frontier/open-letters
 *   /api/frontier/funding-rounds
 *   /api/frontier/compute-buildouts
 */
import { useEffect, useState } from 'react'
import {
  Gavel, UserMinus, FileText, DollarSign, Server, RefreshCw, ExternalLink,
  Briefcase, Calendar, Github, Cpu, Zap, Ban, MessageSquare,
  ShieldCheck, Skull, HardDrive, Bot, Crosshair, Newspaper, TrendingUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type Tab = 'lawsuits' | 'departures' | 'letters' | 'funding' | 'compute' | 'acquisitions' | 'conferences' | 'oss' | 'chips' | 'energy' | 'bans' | 'testimonies' | 'evals' | 'graveyard' | 'clusters' | 'agents' | 'defence' | 'data_deals' | 'hypergrowth'

interface Lawsuit {
  id: string; title: string; filed: string; status: string
  plaintiff: string; defendant: string; venue: string; claim: string
  summary: string; url: string
}
interface Departure {
  id: string; name: string; date: string
  from_org: string; to_org: string; role_at_exit: string; category: string
  summary: string; url: string
}
interface OpenLetter {
  id: string; title: string; date: string
  publisher: string; signatories: string; topic: string
  summary: string; url: string
}
interface FundingRound {
  id: string; lab: string; date: string; round: string
  amount_usd: number; valuation_usd: number | null
  lead_investors: string[]; summary: string; url: string
}
interface Buildout {
  id: string; title: string; announced: string; operator: string
  location: string; scale: string; status: string
  summary: string; url: string
}
interface Acquisition {
  id: string; date: string; acquirer: string; target: string
  type: string; value_usd: number | null; summary: string; url: string
}
interface Conference {
  id: string; name: string; date: string; location: string
  category: string; summary: string; url: string
}
interface OssProject {
  id: string; name: string; category: string; stewards: string
  stars: number; language: string; summary: string; url: string
}
interface Chip {
  id: string; vendor: string; chip: string; released: string
  process: string; perf: string; summary: string
}
interface EnergyDeal {
  id: string; date: string; deal: string; scope: string; summary: string; url: string
}
interface Ban {
  id: string; date: string; jurisdiction: string; subject: string
  type: string; status: string; summary: string; url: string
}
interface Testimony {
  id: string; date: string; witness: string; venue: string
  topic: string; summary: string; url: string
}
interface AisiEval {
  id: string; date: string; evaluator: string; subject: string
  finding: string; summary: string; url: string
}
interface DeadCo {
  id: string; date: string; company: string; outcome: string
  raised_usd: number; killed_by: string; summary: string; url: string
}
interface NamedCluster {
  id: string; name: string; operator: string; location: string
  status: string; scale: string; summary: string
}
interface AgentLaunch {
  id: string; date: string; agent: string; lab: string
  category: string; summary: string
}
interface DefenceOrg {
  id: string; org: string; country: string; category: string
  founded: number; summary: string; url: string
}
interface DataDeal {
  id: string; date: string; publisher: string; lab: string
  value_usd: number | null; duration: string; summary: string; url: string
}
interface HyperApp {
  id: string; name: string; company: string; country: string
  category: string; growth_metric: string; summary: string
}

function fmtBig(n: number | null | undefined): string {
  if (!n) return '—'
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`
  return `$${n.toLocaleString()}`
}

const TABS: { key: Tab; label: string; icon: typeof Gavel; tone: string }[] = [
  { key: 'lawsuits',     label: 'Lawsuits',          icon: Gavel,      tone: 'text-rose-light' },
  { key: 'acquisitions', label: 'M&A',               icon: Briefcase,  tone: 'text-emerald' },
  { key: 'departures',   label: 'Departures',        icon: UserMinus,  tone: 'text-amber-light' },
  { key: 'letters',      label: 'Open Letters',      icon: FileText,   tone: 'text-blue-light' },
  { key: 'funding',      label: 'Funding',           icon: DollarSign, tone: 'text-emerald' },
  { key: 'compute',      label: 'Compute Buildouts', icon: Server,     tone: 'text-purple-light' },
  { key: 'conferences',  label: 'Conferences',       icon: Calendar,   tone: 'text-cyan' },
  { key: 'oss',          label: 'Open Source',       icon: Github,     tone: 'text-text-secondary' },
  { key: 'chips',        label: 'Compute Pipeline',  icon: Cpu,        tone: 'text-amber-light' },
  { key: 'energy',       label: 'Energy Deals',      icon: Zap,            tone: 'text-rose-light' },
  { key: 'bans',         label: 'Bans / Restrictions', icon: Ban,          tone: 'text-rose-light' },
  { key: 'testimonies',  label: 'Policy Testimonies', icon: MessageSquare, tone: 'text-blue-light' },
  { key: 'evals',        label: 'AISI Evals',        icon: ShieldCheck,    tone: 'text-emerald' },
  { key: 'graveyard',    label: 'Startup Graveyard', icon: Skull,          tone: 'text-rose-light' },
  { key: 'clusters',     label: 'Named Clusters',    icon: HardDrive,      tone: 'text-purple-light' },
  { key: 'agents',       label: 'Agents Launched',   icon: Bot,            tone: 'text-amber-light' },
  { key: 'defence',      label: 'Defence AI',        icon: Crosshair,      tone: 'text-rose-light' },
  { key: 'data_deals',   label: 'Data Deals',        icon: Newspaper,      tone: 'text-blue-light' },
  { key: 'hypergrowth',  label: 'Hypergrowth Apps',  icon: TrendingUp,     tone: 'text-emerald' },
]

export default function IndustryWatchPage() {
  const [tab, setTab] = useState<Tab>('lawsuits')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Record<Tab, any>>({} as any)

  const refresh = (which?: Tab) => {
    setLoading(true)
    const wanted = which ? [which] : (TABS.map(t => t.key) as Tab[])
    Promise.all(wanted.map(t => {
      const path = {
        lawsuits:     '/api/frontier/lawsuits',
        departures:   '/api/frontier/departures',
        letters:      '/api/frontier/open-letters',
        funding:      '/api/frontier/funding-rounds',
        compute:      '/api/frontier/compute-buildouts',
        acquisitions: '/api/frontier/acquisitions',
        conferences:  '/api/frontier/conferences',
        oss:          '/api/frontier/oss-projects',
        chips:        '/api/frontier/compute-pipeline',
        energy:       '/api/frontier/energy-deals',
        bans:         '/api/frontier/bans-restrictions',
        testimonies:  '/api/frontier/policy-testimonies',
        evals:        '/api/frontier/aisi-evals',
        graveyard:    '/api/frontier/startup-graveyard',
        clusters:     '/api/frontier/named-clusters',
        agents:       '/api/frontier/agents-launches',
        defence:      '/api/frontier/defence-ai',
        data_deals:   '/api/frontier/data-deals',
        hypergrowth:  '/api/frontier/hypergrowth-apps',
      }[t]
      return api.get(path).then(r => [t, r.data] as const).catch(() => [t, null] as const)
    })).then(rs => {
      setData(prev => {
        const out = { ...prev } as Record<Tab, any>
        for (const [t, d] of rs) if (d) out[t] = d
        return out
      })
    }).finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const totals = {
    lawsuits:     data.lawsuits?.count ?? '—',
    departures:   data.departures?.count ?? '—',
    letters:      data.letters?.count ?? '—',
    funding:      data.funding?.count ?? '—',
    compute:      data.compute?.count ?? '—',
    acquisitions: data.acquisitions?.count ?? '—',
    conferences:  data.conferences?.count ?? '—',
    oss:          data.oss?.count ?? '—',
    chips:        data.chips?.count ?? '—',
    energy:       data.energy?.count ?? '—',
    bans:         data.bans?.count ?? '—',
    testimonies:  data.testimonies?.count ?? '—',
    evals:        data.evals?.count ?? '—',
    graveyard:    data.graveyard?.count ?? '—',
    clusters:     data.clusters?.count ?? '—',
    agents:       data.agents?.count ?? '—',
    defence:      data.defence?.count ?? '—',
    data_deals:   data.data_deals?.count ?? '—',
    hypergrowth:  data.hypergrowth?.count ?? '—',
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Gavel size={18} className="text-rose-light" /> INDUSTRY WATCH
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          lawsuits · people moves · open letters · funding · compute
        </span>
        <button type="button" onClick={() => refresh()} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map(t => {
          const Icon = t.icon
          const active = tab === t.key
          const count = totals[t.key]
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={clsx(
                'px-3 py-1.5 text-xs flex items-center gap-1.5 border-b-2 transition-colors',
                active
                  ? 'border-purple text-text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}>
              <Icon size={12} className={active ? t.tone : ''} />
              <span>{t.label}</span>
              <span className="text-[10px] font-mono opacity-70">{count}</span>
            </button>
          )
        })}
      </div>

      {tab === 'lawsuits'     && <LawsuitsView     rows={data.lawsuits?.lawsuits           || []} />}
      {tab === 'departures'   && <DeparturesView   rows={data.departures?.departures       || []} />}
      {tab === 'letters'      && <LettersView      rows={data.letters?.letters             || []} />}
      {tab === 'funding'      && <FundingView      payload={data.funding} />}
      {tab === 'compute'      && <ComputeView      rows={data.compute?.buildouts           || []} />}
      {tab === 'acquisitions' && <AcquisitionsView rows={data.acquisitions?.acquisitions   || []} />}
      {tab === 'conferences'  && <ConferencesView  rows={data.conferences?.conferences     || []} />}
      {tab === 'oss'          && <OssView          rows={data.oss?.projects                || []} />}
      {tab === 'chips'        && <ChipsView        rows={data.chips?.chips                 || []} />}
      {tab === 'energy'       && <EnergyView       rows={data.energy?.deals                || []} />}
      {tab === 'bans'         && <BansView         rows={data.bans?.bans                   || []} />}
      {tab === 'testimonies'  && <TestimoniesView  rows={data.testimonies?.testimonies     || []} />}
      {tab === 'evals'        && <EvalsRunView     rows={data.evals?.evals                 || []} />}
      {tab === 'graveyard'    && <GraveyardView    rows={data.graveyard?.companies         || []} />}
      {tab === 'clusters'     && <ClustersView     rows={data.clusters?.clusters           || []} />}
      {tab === 'agents'       && <AgentsView       rows={data.agents?.launches             || []} />}
      {tab === 'defence'      && <DefenceView      rows={data.defence?.orgs                || []} />}
      {tab === 'data_deals'   && <DataDealsView    rows={data.data_deals?.deals            || []} />}
      {tab === 'hypergrowth'  && <HyperAppsView    rows={data.hypergrowth?.apps            || []} />}
    </div>
  )
}

function HyperAppsView({ rows }: { rows: HyperApp[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map(a => (
        <article key={a.id} className="card p-3">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary flex-1">{a.name}</h3>
            <span className="text-[10px] text-text-muted">{a.country}</span>
          </div>
          <div className="text-[10px] text-purple-light mb-1 font-mono">{a.company}</div>
          <div className="flex flex-wrap gap-1 mb-2">
            <span className="badge text-[9px] bg-emerald/15 text-emerald border border-emerald/30">{a.category}</span>
          </div>
          <div className="text-[11px] text-amber-light font-mono mb-2">{a.growth_metric}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{a.summary}</p>
        </article>
      ))}
    </div>
  )
}

function DefenceView({ rows }: { rows: DefenceOrg[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map(d => (
        <article key={d.id} className="card p-3">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary flex-1">{d.org}</h3>
            <span className="text-[10px] text-text-muted">{d.country}</span>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30">{d.category}</span>
            <span className="badge text-[9px] bg-surface-raised text-text-muted">est. {d.founded}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{d.summary}</p>
          <a href={d.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function DataDealsView({ rows }: { rows: DataDeal[] }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Publisher</th>
            <th className="text-left px-3 py-2">Lab</th>
            <th className="text-right px-3 py-2">Value</th>
            <th className="text-left px-3 py-2">Duration</th>
            <th className="text-left px-3 py-2">Summary</th>
            <th className="text-left px-3 py-2"><span className="sr-only">Reference</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(d => (
            <tr key={d.id} className="hover:bg-surface-raised/40">
              <td className="px-3 py-1.5 font-mono text-text-muted">{d.date}</td>
              <td className="px-3 py-1.5 font-semibold text-text-primary">{d.publisher}</td>
              <td className="px-3 py-1.5 text-purple-light">{d.lab}</td>
              <td className="px-3 py-1.5 font-mono text-right text-emerald">{fmtBig(d.value_usd)}</td>
              <td className="px-3 py-1.5 text-text-secondary">{d.duration}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary leading-tight">{d.summary}</td>
              <td className="px-3 py-1.5">
                <a href={d.url} target="_blank" rel="noreferrer" aria-label="Reference">
                  <ExternalLink size={10} className="text-blue-light" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function EvalsRunView({ rows }: { rows: AisiEval[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(e => (
        <article key={e.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{e.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{e.subject}</h3>
            <span className="badge text-[9px] bg-emerald/15 text-emerald border border-emerald/30 shrink-0">{e.evaluator}</span>
          </div>
          <div className="text-[11px] text-rose-light font-semibold mb-2">{e.finding}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{e.summary}</p>
          <a href={e.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function GraveyardView({ rows }: { rows: DeadCo[] }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Company</th>
            <th className="text-left px-3 py-2">Outcome</th>
            <th className="text-right px-3 py-2">Raised</th>
            <th className="text-left px-3 py-2">Killed by</th>
            <th className="text-left px-3 py-2">Summary</th>
            <th className="text-left px-3 py-2"><span className="sr-only">Reference</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(c => (
            <tr key={c.id} className="hover:bg-surface-raised/40">
              <td className="px-3 py-1.5 font-mono text-text-muted">{c.date}</td>
              <td className="px-3 py-1.5 font-semibold text-text-primary">{c.company}</td>
              <td className="px-3 py-1.5 text-rose-light">{c.outcome}</td>
              <td className="px-3 py-1.5 font-mono text-right text-amber-light">{fmtBig(c.raised_usd)}</td>
              <td className="px-3 py-1.5 text-text-secondary">{c.killed_by}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary leading-tight">{c.summary}</td>
              <td className="px-3 py-1.5">
                <a href={c.url} target="_blank" rel="noreferrer" aria-label="Reference">
                  <ExternalLink size={10} className="text-blue-light" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ClustersView({ rows }: { rows: NamedCluster[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(c => (
        <article key={c.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{c.name}</h3>
            <span className="badge text-[9px] bg-purple/15 text-purple-light border border-purple/30 shrink-0">{c.status}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-1 font-mono">
            {c.operator} · {c.location}
          </div>
          <div className="text-[11px] text-emerald font-semibold mb-2 font-mono">{c.scale}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{c.summary}</p>
        </article>
      ))}
    </div>
  )
}

function AgentsView({ rows }: { rows: AgentLaunch[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map(a => (
        <article key={a.id} className="card p-3">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-mono text-amber-light">{a.date}</span>
            <h3 className="text-sm font-semibold text-text-primary flex-1 min-w-[40%]">{a.agent}</h3>
            <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/30">{a.category}</span>
          </div>
          <div className="text-[10px] text-purple-light font-mono mb-1.5">{a.lab}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{a.summary}</p>
        </article>
      ))}
    </div>
  )
}

function BansView({ rows }: { rows: Ban[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(b => (
        <article key={b.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{b.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{b.subject}</h3>
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30 shrink-0">{b.status}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-1 font-mono">
            {b.jurisdiction} · <span className="text-purple-light">{b.type}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{b.summary}</p>
          <a href={b.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function TestimoniesView({ rows }: { rows: Testimony[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(t => (
        <article key={t.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{t.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{t.witness}</h3>
          </div>
          <div className="text-[10px] text-text-muted mb-1 font-mono">
            {t.venue} · <span className="text-purple-light">{t.topic}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{t.summary}</p>
          <a href={t.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function ChipsView({ rows }: { rows: Chip[] }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Chip</th>
            <th className="text-left px-3 py-2">Vendor</th>
            <th className="text-left px-3 py-2">Released</th>
            <th className="text-left px-3 py-2">Process</th>
            <th className="text-left px-3 py-2">Performance</th>
            <th className="text-left px-3 py-2">Summary</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(c => (
            <tr key={c.id} className="hover:bg-surface-raised/40">
              <td className="px-3 py-1.5 font-semibold text-text-primary">{c.chip}</td>
              <td className="px-3 py-1.5 text-purple-light">{c.vendor}</td>
              <td className="px-3 py-1.5 font-mono text-text-muted">{c.released}</td>
              <td className="px-3 py-1.5 font-mono text-amber-light">{c.process}</td>
              <td className="px-3 py-1.5 font-mono text-emerald">{c.perf}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary leading-tight">{c.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function EnergyView({ rows }: { rows: EnergyDeal[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(d => (
        <article key={d.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{d.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{d.deal}</h3>
          </div>
          <div className="text-[11px] text-emerald font-semibold mb-2 font-mono">{d.scope}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{d.summary}</p>
          <a href={d.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function AcquisitionsView({ rows }: { rows: Acquisition[] }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Acquirer</th>
            <th className="text-left px-3 py-2">Target</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-right px-3 py-2">Value</th>
            <th className="text-left px-3 py-2">Summary</th>
            <th className="text-left px-3 py-2"><span className="sr-only">Reference</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(a => (
            <tr key={a.id} className="hover:bg-surface-raised/40">
              <td className="px-3 py-1.5 font-mono text-text-muted">{a.date}</td>
              <td className="px-3 py-1.5 font-semibold text-purple-light">{a.acquirer}</td>
              <td className="px-3 py-1.5 text-emerald font-semibold">{a.target}</td>
              <td className="px-3 py-1.5 text-text-secondary">{a.type}</td>
              <td className="px-3 py-1.5 font-mono text-right text-amber-light">{fmtBig(a.value_usd)}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary leading-tight">{a.summary}</td>
              <td className="px-3 py-1.5">
                <a href={a.url} target="_blank" rel="noreferrer" aria-label="Reference">
                  <ExternalLink size={10} className="text-blue-light" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ConferencesView({ rows }: { rows: Conference[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(c => (
        <article key={c.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{c.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{c.name}</h3>
            <span className="badge text-[9px] bg-cyan/15 text-cyan border border-cyan/30 shrink-0">{c.category}</span>
          </div>
          <div className="text-[10px] text-text-muted font-mono mb-2">{c.location}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{c.summary}</p>
          <a href={c.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function OssView({ rows }: { rows: OssProject[] }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Project</th>
            <th className="text-left px-3 py-2">Category</th>
            <th className="text-left px-3 py-2">Stewards</th>
            <th className="text-right px-3 py-2">Stars</th>
            <th className="text-left px-3 py-2">Lang</th>
            <th className="text-left px-3 py-2">Summary</th>
            <th className="text-left px-3 py-2"><span className="sr-only">Repo</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(p => (
            <tr key={p.id} className="hover:bg-surface-raised/40">
              <td className="px-3 py-1.5 font-semibold text-text-primary">{p.name}</td>
              <td className="px-3 py-1.5">
                <span className="badge text-[9px] bg-surface-raised text-text-muted">{p.category}</span>
              </td>
              <td className="px-3 py-1.5 text-purple-light">{p.stewards}</td>
              <td className="px-3 py-1.5 font-mono text-right text-amber-light">{p.stars.toLocaleString()}★</td>
              <td className="px-3 py-1.5 text-text-secondary">{p.language}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary leading-tight">{p.summary}</td>
              <td className="px-3 py-1.5">
                <a href={p.url} target="_blank" rel="noreferrer" aria-label="GitHub repo">
                  <Github size={10} className="text-text-muted hover:text-text-primary" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LawsuitsView({ rows }: { rows: Lawsuit[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(l => (
        <article key={l.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{l.filed}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{l.title}</h3>
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30 shrink-0">{l.status}</span>
          </div>
          <div className="text-[10px] text-text-muted font-mono mb-1">
            {l.plaintiff} → {l.defendant} · {l.venue}
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            <span className="badge text-[9px] bg-surface-raised text-text-muted">{l.claim}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{l.summary}</p>
          <a href={l.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function DeparturesView({ rows }: { rows: Departure[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(d => (
        <article key={d.id} className="card p-4">
          <div className="flex items-start gap-2 mb-1">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{d.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{d.name}</h3>
            <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/30 shrink-0">{d.category}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-2">
            <span className="text-text-secondary">{d.from_org}</span>
            {' → '}
            <span className="text-emerald">{d.to_org}</span>
            {' · '}{d.role_at_exit}
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{d.summary}</p>
          <a href={d.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function LettersView({ rows }: { rows: OpenLetter[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(l => (
        <article key={l.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{l.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{l.title}</h3>
            <span className="badge text-[9px] bg-blue/15 text-blue-light border border-blue/30 shrink-0">{l.topic}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-2">
            <span className="text-text-secondary">{l.publisher}</span>
            {l.signatories && <> · {l.signatories}</>}
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{l.summary}</p>
          <a href={l.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function FundingView({ payload }: { payload: { rounds: FundingRound[]; total_raised: number; by_lab_total: Record<string, number> } | undefined }) {
  const rounds = payload?.rounds || []
  const total  = payload?.total_raised || 0
  const byLab  = payload?.by_lab_total || {}
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Total raised tracked</div>
          <div className="text-2xl font-black font-mono mt-1 text-emerald">{fmtBig(total)}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Rounds tracked</div>
          <div className="text-2xl font-black font-mono mt-1 text-blue-light">{rounds.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Top lab</div>
          <div className="text-sm font-bold text-purple-light mt-1">
            {Object.keys(byLab)[0] || '—'}{' '}
            <span className="text-text-muted font-mono text-xs">
              {fmtBig(Object.values(byLab)[0])}
            </span>
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Lab</th>
              <th className="text-left px-3 py-2">Round</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">Valuation</th>
              <th className="text-left px-3 py-2">Lead investors</th>
              <th className="text-left px-3 py-2"><span className="sr-only">Reference link</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rounds.map(r => (
              <tr key={r.id} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5 font-mono text-text-muted">{r.date}</td>
                <td className="px-3 py-1.5 font-semibold text-text-primary">{r.lab}</td>
                <td className="px-3 py-1.5 text-text-secondary">{r.round}</td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald">{fmtBig(r.amount_usd)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-light">{fmtBig(r.valuation_usd)}</td>
                <td className="px-3 py-1.5 text-[10px] text-text-secondary">{r.lead_investors.join(', ')}</td>
                <td className="px-3 py-1.5">
                  <a href={r.url} target="_blank" rel="noreferrer" aria-label="Reference">
                    <ExternalLink size={10} className="text-blue-light" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function ComputeView({ rows }: { rows: Buildout[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(b => (
        <article key={b.id} className="card p-4">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-[11px] font-mono text-amber-light shrink-0">{b.announced}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{b.title}</h3>
            <span className="badge text-[9px] bg-purple/15 text-purple-light border border-purple/30 shrink-0">{b.status}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-1 font-mono">
            {b.operator} · {b.location}
          </div>
          <div className="text-[11px] text-emerald font-semibold mb-2">{b.scale}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{b.summary}</p>
          <a href={b.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}
